import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import SubTitle from "components/SubTitle";

import { Formik } from "formik";
import * as Yup from "yup";
import { useRole } from "hooks/useRole";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetReportSummaryQuery,
  useUpdateReportSummaryByControllerMutation,
} from "features/api/reports/reportsSlice";

import { useGetTextForCodeValue } from "hooks/useDataCodeValue";
import { toast } from "react-toastify";
import {
  ERROR_MESSAGE,
  UserRole,
} from "components/Reports/reportAssets/assets";
import { Spinner } from "react-bootstrap";
import { useLocationForReportType } from "hooks/useLocationForReportType";
import { useReports } from "hooks/useReports";
import FormSummarySection from "./FormSummaryStepComponents/FormSummarySection";
import FormFeedbackSection from "./FormSummaryStepComponents/FormFeedbackSection";
import ButtonsSEctionReportDetails from "../buttonsSetionReportDetails";
import { FormStepsProps } from "./FormStepsProps";
import {
  AddOrUpdateeReportSummaryBody,
  FormControlsValues,
  SummaryData,
} from "./FormSummaryStepComponents/SummaryStepTypes";

const FormSummaryStep = ({
  currStep,
  setModalShow,
  backNavigation,
}: FormStepsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locationString = useLocationForReportType();
  const isMessagesScreen = locationString === "followup-messages";
  const isReadonly = locationString === "followup" || isMessagesScreen;
  const [isBackNavigation, setIsBackNavigation] = useState(false);
  const { getReportId } = useReports();
  const reportId = getReportId();
  const role = useRole();
  const isControlCenter = role === UserRole.CONTROLCENTER;
  const isSenior = role === UserRole.SENIOR;

  const { data: report }: { data: SummaryData } = useGetReportSummaryQuery({
    role,
    reportId,
  });

  const [updateReportSummaryByController] =
    useUpdateReportSummaryByControllerMutation();

  const formikPropsObject = {
    initialValues: {
      reportId,
      offenceTypeName: useGetTextForCodeValue(
        report?.offenseTypeID,
        "offences"
      ),
      decisionTypeName: useGetTextForCodeValue(
        report?.decisionTypeID,
        "decisions"
      ),
      // Carry the stable decision code so display-localized matching uses the
      // code, not the localized label (Pitfall 6).
      decisionTypeID: report?.decisionTypeID,

      rejectionReasons: useGetTextForCodeValue(
        report?.rejectionReasonID,
        "rejectionReasonID"
      ),

      feedBackToVolunteer: report?.feedBackToVolunteer ?? "",
      commentToRegularController: report?.commentToRegularController,
      reportForImmediateTreatment: report?.reportForImmediateTreatment,
      recommendForInformationalMaterial:
        report?.recommendForInformationalMaterial,
      reportSuggestionId: report?.reportSuggestionId,
    },
    enableReinitialize: true, // Enable reinitialization when initialValues change

    validationSchema: Yup.object().shape({
      feedBackToVolunteer: Yup.string()
        .max(1000, t("חריגה מכמות התווים"))
        .nullable(),
      commentToRegularController: Yup.string()
        .max(240, t("חריגה מכמות התווים"))
        .nullable(),
      reportForImmediateTreatment: Yup.boolean().oneOf([true, false]),
      recommendForInformationalMaterial: Yup.boolean().oneOf([true, false]),
    }),
    onSubmit: async (
      values: FormControlsValues,
      { setSubmitting, resetForm }: any
    ) => {
      if (isReadonly) {
        isBackNavigation ? backNavigation() : navigate("/reports/followup");
        return;
      }
      const body: AddOrUpdateeReportSummaryBody = {
        reportId: Number(reportId),
        feedBackToVolunteer: values.feedBackToVolunteer,
        commentToRegularController: values.commentToRegularController,
        reportForImmediateTreatment: values.reportForImmediateTreatment,
        recommendForInformationalMaterial:
          values.recommendForInformationalMaterial,
      };
      const isStatusUpdate = !isBackNavigation;
      // submit to server
      // isStatusUpdate say to server if update status or not - when user go back we want to save the data but not to update the status
      try {
        const res = await updateReportSummaryByController({
          body,
          role,
          isStatusUpdate,
        }).unwrap();

        if (res?.error?.data?.[0]?.errorMessage) {
          toast.error(res.error.data[0].errorMessage);
          return;
        }
        isBackNavigation ? backNavigation() : navigate("/reports/followup");
      } catch (error) {
        console.error(error);
        toast.error(ERROR_MESSAGE);
      }
      setSubmitting(false);
      resetForm();
    },
  };

  const getSubmitButtonText = () => {
    if (
      report?.isAllowToReturnReport &&
      report?.missingDetails &&
      ((isControlCenter && report.hasSeniorController) ||
        (isSenior && report.hasRegularController))
    )
      return t("returnToController");
    return t("labelFinish");
  };

  return report ? (
    <>
      <SubTitle className="default-vertical-padding">
        {t("reportSummary")}
      </SubTitle>
      <Formik {...formikPropsObject}>
        {(formikProps) => {
          return (
            <>
              <FormSummarySection formikProps={formikProps} />
              <FormFeedbackSection formikProps={formikProps} role={role} />
              {!isMessagesScreen && (
                <ButtonsSEctionReportDetails
                  handleSubmit={formikProps.handleSubmit}
                  setModalShow={setModalShow}
                  setIsBackNavigation={setIsBackNavigation}
                  approveBtnLabel={getSubmitButtonText()}
                  currStep={currStep}
                  isSubmitButtonDisable={
                    !isControlCenter &&
                    report?.missingDetails &&
                    report?.isVolunteerDisable
                  }
                  isSubmitButtonShow={
                    !isControlCenter || (isControlCenter && !isReadonly)
                  }
                />
              )}
            </>
          );
        }}
      </Formik>
    </>
  ) : (
    <Spinner animation="border" variant="secondary" />
  );
};
export default FormSummaryStep;
